import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ClerkProvider, SignedIn, SignedOut, SignIn, SignInButton, SignUpButton, UserButton, useAuth, useUser } from '@clerk/clerk-react';
import { plannerEvent, type PlannerAction } from '../core/events';
import { ApiError, call, localToday, type GetToken, type Me } from './api';
import { createAnalytics, nullAnalytics, type Analytics } from './analytics/client';
import { clerkConfiguration } from './auth-config';
import { LabsNav } from '@240tutoring/labs-ui/labs-nav';
import { Unavailable } from './components/Unavailable';
import { SubscriptionPreview } from './components/SubscriptionPreview';
import { NewPlan } from './components/NewPlan';
import { PlanList } from './components/PlanList';
import { PlanPage } from './components/PlanEditor';
import { TopicReader } from './components/TopicReader';
import { WeeklyView } from './components/WeeklyView';
import { MyWeek } from './components/MyWeek';
import type { UnitTopic } from '../core/topics';
import { planbookOn } from '../core/home';
import { linkTo, MY_WEEK_HREF, navigate, NEW_PLAN_HREF, parseRoute, planHref, PLANS_HREF, thisWeekHref, topicHref, weekHref, type Route } from './navigation';

const accessFailure = (reason: string) => ['disabled', 'subscription_required', 'subscription_unavailable'].includes(reason);

export function PlannerApp({ auth, headerAction, analytics: injected }: { auth: { userId: string; email?: string | null; getToken: GetToken }; headerAction?: ReactNode; analytics?: Analytics }) {
  const tokenRef = useRef(auth.getToken); tokenRef.current = auth.getToken;
  const getToken = useCallback(() => tokenRef.current(), []);
  const analyticsRef = useRef<Analytics>(injected ?? nullAnalytics());
  const viewed = useRef<string | null>(null);
  const killShown = useRef(false);
  const subscriptionShown = useRef(false);
  const [me, setMe] = useState<Me | null>(null);
  const [stage, setStage] = useState<'loading' | 'ready' | 'denied'>('loading');
  const [denied, setDenied] = useState('unreachable');
  const [boot, setBoot] = useState(0);
  const track = useCallback((action: PlannerAction, properties: Record<string, string | number | boolean | string[]> = {}) => { try { analyticsRef.current.track(plannerEvent(action, properties)); } catch { /* Analytics is optional. */ } }, []);
  useEffect(() => {
    let live = true;
    const adapter = injected ?? createAnalytics(auth.userId, auth.email ?? null, getToken);
    analyticsRef.current = adapter;
    setStage('loading');
    void (async () => {
      try {
        const identity = await call<Me>(getToken, '/api/me');
        if (!live) return;
        setMe(identity); setStage('ready');
        if (viewed.current !== identity.userId) { viewed.current = identity.userId; track('page_viewed'); }
      } catch (failure) {
        if (!live) return;
        const e = failure as ApiError;
        setDenied(accessFailure(e.reason) ? e.reason : e.status === 401 ? 'session_invalid' : 'unreachable'); setStage('denied');
      }
    })();
    return () => { live = false; if (!injected) adapter.dispose(); analyticsRef.current = nullAnalytics(); };
  }, [auth.userId, auth.email, boot, getToken, injected, track]);
  useEffect(() => {
    const shown = stage === 'denied' && denied === 'disabled';
    if (shown && !killShown.current) track('kill_switch_shown');
    killShown.current = shown;
    const previewShown = stage === 'denied' && denied === 'subscription_required';
    if (previewShown && !subscriptionShown.current) track('subscription_preview_shown');
    subscriptionShown.current = previewShown;
  }, [stage, denied, track]);
  const route = useRoute();
  // Where a topic was opened from, so its back link returns there; the plan list otherwise.
  const topicReturn = useRef<{ href: string; planId: string; unitId: string; title: string } | null>(null);
  const fresh = useRef<string | null>(null);
  const openTopic = (topic: UnitTopic, unitId: string, href: string, planId: string) => {
    topicReturn.current = { href, planId, unitId, title: topic.title }; navigate(topicHref(topic.id));
  };
  const myWeek = me?.myWeek === true;
  const dayItems = me?.dayItems === true;
  const collabAttachments = me?.collabAttachments === true;
  // With My week and day items on, the planbook is where a teacher plans: the header names it
  // and Plans, and a plan's own weekly view gives way to the planbook at this week.
  const planbook = planbookOn(myWeek, dayItems);
  const retiredWeek = stage === 'ready' && planbook && route.name === 'week';
  useEffect(() => { if (retiredWeek) navigate(thisWeekHref(localToday()), true); }, [retiredWeek]);
  const section = route.name === 'my_week' ? 'planbook' : ['plans', 'new', 'plan'].includes(route.name) ? 'plans' : null;
  const retry = () => { track('access_retry_clicked', { reason: denied }); setBoot(n => n + 1); };
  return <><LabsNav name="240 Planner" onLabsClick={placement => track('labs_clicked', { placement })} account={headerAction}>
      {stage === 'ready' && planbook && <nav className="preview-nav-links" aria-label="Planner">
        <a className="preview-nav-link" {...linkTo(MY_WEEK_HREF)} aria-current={section === 'planbook' ? 'page' : undefined}>Planbook</a>
        <a className="preview-nav-link" {...linkTo(PLANS_HREF)} aria-current={section === 'plans' ? 'page' : undefined}>Plans</a>
      </nav>}
    </LabsNav>
    <div className="app-shell">
    <main>{stage === 'loading' && <div className="notice" role="status">Getting things ready…</div>}
      {stage === 'denied' && (denied === 'subscription_required' ? <SubscriptionPreview onRetry={retry} onTrack={track} /> : <Unavailable reason={denied} onRetry={retry} />)}
      {stage === 'ready' && <>{me?.fixtures && <div className="fixture-banner" role="status"><strong>Sample content mode.</strong> Standards and topics use fixed examples, not a live search.</div>}
        {(route.name === 'home' || route.name === 'plans') && <PlanList key={route.name} getToken={getToken} onTrack={track} openLast={route.name === 'home'} myWeek={myWeek} dayItems={dayItems} onNew={() => navigate(NEW_PLAN_HREF)} />}
        {route.name === 'my_week' && <MyWeek getToken={getToken} myWeek={myWeek} dayItems={dayItems} collabAttachments={collabAttachments} at={route.at} onTrack={track} />}
        {route.name === 'new' && <NewPlan getToken={getToken} today={localToday()} onCancel={() => navigate(PLANS_HREF)} onCreated={(id, isFresh) => { fresh.current = isFresh ? id : null; navigate(planHref(id), true); }} />}
        {route.name === 'plan' && <PlanPage key={route.id} planId={route.id} getToken={getToken} fresh={fresh.current === route.id} planbook={planbook} onDeleted={() => navigate(PLANS_HREF, true)}
          onOpenTopic={(topic, unitId) => openTopic(topic, unitId, planHref(route.id), route.id)} />}
        {route.name === 'week' && !planbook && <WeeklyView key={route.id} planId={route.id} getToken={getToken} myWeek={myWeek} dayItems={dayItems} onTrack={track}
          onOpenTopic={(topic, unitId) => openTopic(topic, unitId, weekHref(route.id), route.id)} />}
        {route.name === 'topic' && <TopicReader key={route.id} id={route.id} name={topicReturn.current?.title} getToken={getToken} onTrack={track}
          eventProperties={topicReturn.current ? { plan_id: topicReturn.current.planId, unit_id: topicReturn.current.unitId } : {}}
          onBack={() => navigate(topicReturn.current?.href ?? '/')} />}
      </>}
    </main><footer className="site-footer"><span>Made for the moments between lessons.</span><span>240 Labs · 240 Planner</span></footer>
  </div></>;
}
/** The current route, following Back, Forward, and in-app navigation. */
function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const update = () => { setRoute(parseRoute(window.location.pathname)); window.scrollTo(0, 0); };
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  return route;
}
function ClerkAuthed() {
  const { getToken, userId, sessionId } = useAuth();
  const { isLoaded, user } = useUser();
  if (!isLoaded || !user || !userId) return <main className="auth-page" role="status">Checking your sign-in…</main>;
  return <PlannerApp key={`${userId}:${sessionId}`} auth={{ getToken, userId, email: user.primaryEmailAddress?.emailAddress ?? null }} headerAction={<UserButton />} />;
}
export default function App() {
  const key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const testIdentity = !key && import.meta.env.VITE_E2E_IDENTITY;
  if (testIdentity) return <><div className="test-banner">Test identity build · Sign-in is bypassed. Not for production.</div><PlannerApp auth={{ userId: testIdentity.split(':')[0]!, email: testIdentity.split(':')[1] ?? null, getToken: async () => testIdentity }} /></>;
  if (!key) return <main className="auth-page"><div className="eyebrow">240 LABS</div><h1>240 Planner</h1><p>Sign-in is not configured for this build.</p><p>Set the Clerk publishable key and rebuild to continue.</p></main>;
  const config = clerkConfiguration(window.location.origin, window.location.pathname);
  return <ClerkProvider publishableKey={key} {...config.provider}><SignedOut><main className="auth-page"><div className="eyebrow">240 LABS</div><h1>240 Planner</h1><p>Sign in to plan your year: your course’s standards, across your school calendar.</p><div className="sign-in-controls">{config.hosted ? <><SignInButton mode="redirect"><button className="primary">Sign in with your 240 account</button></SignInButton><SignUpButton mode="redirect"><button className="text-button">Create an account</button></SignUpButton></> : <SignIn routing="hash" />}</div></main></SignedOut><SignedIn><ClerkAuthed /></SignedIn></ClerkProvider>;
}
